import api, { apiUpload, fastUpload, uploadWithRetry } from "./api";
import { VideoListResponse, VideoProgress, Video, Folder } from "../types";

export const videoService = {
  async getVideos(
    page = 1,
    limit = 20,
    category?: string,
    search?: string,
    root?: boolean,
  ): Promise<VideoListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (category) params.append("category", category);
    if (search) params.append("search", search);
    if (root) params.append("root", "true");

    const response = await api.get(`/videos?${params}`);
    return response.data;
  },

  async getFolders(): Promise<Folder[]> {
    const response = await api.get("/folders");
    return response.data.folders; // Assuming backend returns { success: true, folders: [...] }
  },

  async getFolderContents(
    folderId: number,
  ): Promise<{ folder: Folder; subfolders: Folder[]; videos: Video[] }> {
    const response = await api.get(`/folders/${folderId}`);
    // Backend returns: { success: true, folder: {...}, videos: [...], subfolders: [...] }
    // But wait, backend returns folder object which INCLUDES videos and subfolders attached to it?
    // Let's check backend implementation again.
    // backend: folder.videos = videos; folder.subfolders = subfolders; res.json({ success: true, folder });
    return response.data;
  },

  async getVideo(videoId: number): Promise<{ video: Video }> {
    const response = await api.get(`/videos/${videoId}`);
    return response.data;
  },

  async getVideoStreamUrl(videoId: number): Promise<{ streamUrl: string }> {
    const response = await api.get(`/videos/${videoId}/stream`);
    return response.data;
  },

  async updateProgress(
    videoId: number,
    data: VideoProgress,
  ): Promise<{ message: string }> {
    const response = await api.post(`/videos/${videoId}/progress`, data);
    return response.data;
  },

  async getWatchedVideos(page = 1, limit = 20): Promise<VideoListResponse> {
    const response = await api.get(
      `/videos/user/watched?page=${page}&limit=${limit}`,
    );
    return response.data;
  },

  async getRecentlyViewedVideos(limit = 10): Promise<VideoListResponse> {
    const response = await api.get(
      `/videos/user/recently-viewed?limit=${limit}`,
    );
    return response.data;
  },

  async uploadVideo(
    formData: FormData,
  ): Promise<{ message: string; video: Video }> {
    console.log("🚀 Starting optimized video upload via videoService...");
    try {
      // Try fast upload first (most uploads succeed on first try)
      const response = await fastUpload("/videos", formData);
      return response.data;
    } catch (error: any) {
      // Only use retry mechanism if it's a network error
      const isNetworkError =
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.code === "ENOTFOUND";
      if (isNetworkError) {
        console.log(
          "Network error detected, falling back to retry mechanism...",
        );
        const response = await uploadWithRetry("/videos", formData);
        return response.data;
      }
      // Re-throw server errors immediately (don't retry)
      throw error;
    }
  },

  async deleteVideo(id: number): Promise<void> {
    await api.delete(`/videos/${id}`);
  },

  async updateViewsCount(
    id: number,
    watchDuration: number,
    totalDuration: number,
  ): Promise<{
    message: string;
    views_count: number;
    watch_duration?: number;
    total_duration?: number;
    min_required?: number;
  }> {
    const response = await api.post(`/videos/${id}/views`, {
      watchDuration,
      totalDuration,
    });
    return response.data;
  },

  // Helper functions for displaying time
  formatDuration(seconds: number | undefined): string {
    // Handle undefined, null or zero values
    if (!seconds || seconds <= 0) {
      return "0:00";
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  },

  formatWatchTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} mins`;
  },
};
